"use client";

import { createContext, type ReactNode, useContext, useMemo } from "react";

/** Root transport implemented by the host app; callers choose an explicit org. */
export type OrgFetchForOrg = (
  orgId: string,
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

/** Fetch-like transport bound to the current route's OrgScopeProvider. */
export type OrgFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

const missingOrgFetchForOrg: OrgFetchForOrg = async () => {
  throw new Error("OrgFetchProvider is required for organization-scoped requests");
};
const missingOrgFetch: OrgFetch = async () => {
  throw new Error("OrgScopeProvider is required for organization-scoped requests");
};

const OrgFetchContext = createContext<OrgFetchForOrg>(missingOrgFetchForOrg);
const OrgScopeContext = createContext<string | null>(null);

export function OrgFetchProvider({
  children,
  orgFetch,
}: {
  children: ReactNode;
  orgFetch: OrgFetchForOrg;
}) {
  return <OrgFetchContext.Provider value={orgFetch}>{children}</OrgFetchContext.Provider>;
}

export function OrgScopeProvider({ children, orgId }: { children: ReactNode; orgId: string }) {
  return <OrgScopeContext.Provider value={orgId}>{children}</OrgScopeContext.Provider>;
}

export function useOrgFetch(): OrgFetch {
  const orgFetchForOrg = useContext(OrgFetchContext);
  const orgId = useContext(OrgScopeContext);
  return useMemo(
    () =>
      orgId
        ? (input: RequestInfo | URL, init?: RequestInit) => orgFetchForOrg(orgId, input, init)
        : missingOrgFetch,
    [orgFetchForOrg, orgId],
  );
}

export function useOrgFetchForOrg(): OrgFetchForOrg {
  return useContext(OrgFetchContext);
}
